import { useEffect, useMemo, useState } from 'react';
import type { Role } from '@flintmint/core-shared/constants';
import type { UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';

import { fetchAdmittedEmailsByUserId } from '@/services/access-list-service';
import { fetchAllTokenUsageMonthlyRollups, type TokenUsageMonthlyRollupDoc } from '@/services/ai-usage-pricing-service';
import { fetchAllUserAccounts } from '@/services/user-accounts-service';
import { fetchAllUsers, type UserDoc } from '@/services/users-service';

export interface UserRow extends UserDoc {
  account: UserAccountDoc | null;
  // No `users` doc yet: admitted and given roles, but has not accepted the agreements.
  agreementsPending: boolean;
  avgMonthlyCostUsd: number | null;
  activeMonthCount: number;
  lastUsageMonth: string | null;
  hasPricingGap: boolean;
}

interface UsersData {
  users: UserDoc[];
  accounts: UserAccountDoc[];
  admittedEmailsByUserId: Map<string, string>;
}

export function useUsers(rolesByUserId: Map<string, Role[]>) {
  const [usersData, setUsersData] = useState<UsersData | null>(null);
  const [monthlyRollups, setMonthlyRollups] = useState<TokenUsageMonthlyRollupDoc[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUsageLoading, setIsUsageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchAllUsers(), fetchAllUserAccounts(), fetchAdmittedEmailsByUserId()])
      .then(([users, accounts, admittedEmailsByUserId]) => {
        if (cancelled) return;
        setUsersData({ users, accounts, admittedEmailsByUserId });
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load users');
        setIsLoading(false);
      });

    fetchAllTokenUsageMonthlyRollups()
      .then((rollups) => {
        if (cancelled) return;
        setMonthlyRollups(rollups);
        setIsUsageLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setUsageError(err instanceof Error ? err.message : 'Failed to load usage');
        setIsUsageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo<UserRow[]>(() => {
    if (!usersData) return [];
    const { users, accounts, admittedEmailsByUserId } = usersData;

    const rollupsByUser: Record<string, TokenUsageMonthlyRollupDoc[]> = {};
    monthlyRollups?.forEach((rollup) => {
      if (!rollupsByUser[rollup.userId]) rollupsByUser[rollup.userId] = [];
      rollupsByUser[rollup.userId].push(rollup);
    });
    const accountsByUserId = new Map(accounts.map((account) => [account.userId, account]));

    const userIds = new Set(users.map((user) => user.userId));
    // A full revoke leaves `roles: []`, which is not a pending admission.
    const pendingUsers: UserDoc[] = [...rolesByUserId]
      .filter(([userId, roles]) => roles.length > 0 && !userIds.has(userId))
      .map(([userId]) => ({ userId, email: admittedEmailsByUserId.get(userId) ?? userId }));

    const result: UserRow[] = [...users, ...pendingUsers].map((user) => {
      const rollups = rollupsByUser[user.userId] ?? [];
      const activeMonths = new Set(rollups.map((rollup) => rollup.month));
      const hasPricingGap = rollups.some((rollup) => rollup.estimatedCostUsd === null);
      const totalCostUsd = rollups.reduce((sum, rollup) => sum + (rollup.estimatedCostUsd ?? 0), 0);

      return {
        ...user,
        account: accountsByUserId.get(user.userId) ?? null,
        agreementsPending: !userIds.has(user.userId),
        avgMonthlyCostUsd: activeMonths.size > 0 && !hasPricingGap ? totalCostUsd / activeMonths.size : null,
        activeMonthCount: activeMonths.size,
        lastUsageMonth: findLastUsageMonth(rollups),
        hasPricingGap,
      };
    });

    result.sort((a, b) => (b.avgMonthlyCostUsd ?? -1) - (a.avgMonthlyCostUsd ?? -1));
    return result;
  }, [usersData, monthlyRollups, rolesByUserId]);

  return { rows, isLoading, isUsageLoading, error, usageError };
}

function findLastUsageMonth(rollups: TokenUsageMonthlyRollupDoc[]): string | null {
  return rollups.reduce<string | null>((lastUsageMonth, rollup) => {
    if (!lastUsageMonth) return rollup.month;
    return rollup.month > lastUsageMonth ? rollup.month : lastUsageMonth;
  }, null);
}
